import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { 
  Bold, 
  Italic, 
  List, 
  ListOrdered, 
  Link, 
  Image, 
  Quote,
  Heading1,
  Heading2,
  Heading3,
  Type,
  Hash,
  Underline
} from "lucide-react";
import { cn } from "@/lib/utils";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export const RichTextEditor = ({ value, onChange, placeholder }: RichTextEditorProps) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [activeFormats, setActiveFormats] = useState<Set<string>>(new Set());
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize editor content and fix typing behavior
  useEffect(() => {
    if (editorRef.current && !isInitialized) {
      editorRef.current.innerHTML = value || '';
      setIsInitialized(true);
    }
  }, [value, isInitialized]);

  // Handle external value changes (but not during typing)
  useEffect(() => {
    if (editorRef.current && isInitialized && editorRef.current.innerHTML !== value) {
      const selection = window.getSelection();
      const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
      
      editorRef.current.innerHTML = value;
      
      // Restore cursor position if possible
      if (range && editorRef.current.contains(range.startContainer)) {
        try {
          selection?.removeAllRanges();
          selection?.addRange(range);
        } catch (e) {
          // Fallback: place cursor at end
          const newRange = document.createRange();
          newRange.selectNodeContents(editorRef.current);
          newRange.collapse(false);
          selection?.removeAllRanges();
          selection?.addRange(newRange);
        }
      }
    }
  }, [value, isInitialized]);

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    updateActiveFormats();
  };

  const updateActiveFormats = () => {
    const formats = new Set<string>();
    if (document.queryCommandState('bold')) formats.add('bold');
    if (document.queryCommandState('italic')) formats.add('italic');
    if (document.queryCommandState('underline')) formats.add('underline');
    setActiveFormats(formats);
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
    updateActiveFormats();
  };

  const handleImageInsert = () => {
    if (imageUrl.trim()) {
      execCommand('insertHTML', `<img src="${imageUrl.trim()}" alt="Blog image" style="max-width: 100%; height: auto; margin: 1rem 0; border-radius: 8px;" />`);
      setImageUrl("");
      setImageDialogOpen(false);
    }
  };

  const handleLinkInsert = () => {
    if (linkUrl.trim()) {
      const selection = window.getSelection();
      const selectedText = selection?.toString();

      // Use custom text if provided, otherwise use selected text or URL
      const displayText = linkText.trim() || selectedText || linkUrl.trim();

      if (selectedText && selectedText.length > 0 && !linkText.trim()) {
        // If text is selected and no custom text provided, create a link with selected text
        execCommand('createLink', linkUrl.trim());
      } else {
        // Insert HTML link with display text
        execCommand('insertHTML', `<a href="${linkUrl.trim()}" target="_blank" rel="noopener noreferrer" style="color: blue; text-decoration: underline;">${displayText}</a>`);
      }

      setLinkUrl("");
      setLinkText("");
      setLinkDialogOpen(false);
    }
  };

  const toolbarButtons = [
    { command: 'bold', icon: Bold, label: 'Bold', active: activeFormats.has('bold') },
    { command: 'italic', icon: Italic, label: 'Italic', active: activeFormats.has('italic') },
    { command: 'underline', icon: Underline, label: 'Underline', active: activeFormats.has('underline') },
    { command: 'insertOrderedList', icon: ListOrdered, label: 'Numbered List' },
    { command: 'insertUnorderedList', icon: List, label: 'Bullet List' },
    { command: 'formatBlock', icon: Heading1, label: 'Heading 1', value: 'h1' },
    { command: 'formatBlock', icon: Heading2, label: 'Heading 2', value: 'h2' },
    { command: 'formatBlock', icon: Heading3, label: 'Heading 3', value: 'h3' },
    { command: 'formatBlock', icon: Type, label: 'Heading 4', value: 'h4' },
    { command: 'formatBlock', icon: Hash, label: 'Heading 5', value: 'h5' },
    { command: 'formatBlock', icon: Hash, label: 'Heading 6', value: 'h6' },
    { command: 'formatBlock', icon: Quote, label: 'Quote', value: 'blockquote' },
  ];

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card">
      <div className="flex flex-wrap gap-1 p-3 border-b border-border bg-muted/30">
        {toolbarButtons.map((button) => (
          <Button
            key={button.command + (button.value || '')}
            variant="ghost"
            size="sm"
            type="button"
            className={cn(
              "h-8 w-8 p-0",
              button.active && "bg-primary text-primary-foreground"
            )}
            onClick={(e) => {
              e.preventDefault();
              button.value ? execCommand(button.command, button.value) : execCommand(button.command);
            }}
            title={button.label}
          >
            <button.icon className="h-4 w-4" />
          </Button>
        ))}
        <div className="w-px h-6 bg-border mx-1" />
        
        {/* Link Dialog */}
        <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              className="h-8 w-8 p-0"
              title="Insert Link"
            >
              <Link className="h-4 w-4" />
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Insert Link</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="link-url">URL</Label>
                <Input
                  id="link-url"
                  placeholder="https://example.com"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleLinkInsert()}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="link-text">Link Text (optional)</Label>
                <Input
                  id="link-text"
                  placeholder="Click here"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleLinkInsert()}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => {
                  setLinkDialogOpen(false);
                  setLinkUrl("");
                  setLinkText("");
                }}>
                  Cancel
                </Button>
                <Button type="button" onClick={handleLinkInsert}>
                  Insert Link
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Image Dialog */}
        <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              className="h-8 w-8 p-0"
              title="Insert Image"
            >
              <Image className="h-4 w-4" />
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Insert Image</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="image-url">Image URL</Label>
                <Input
                  id="image-url"
                  placeholder="https://example.com/image.jpg"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleImageInsert()}
                />
              </div>
              {imageUrl && (
                <div className="space-y-2">
                  <Label>Preview</Label>
                  <div className="border rounded-lg p-2 bg-muted/30">
                    <img 
                      src={imageUrl} 
                      alt="Preview" 
                      className="max-w-full h-auto max-h-40 rounded"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setImageDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="button" onClick={handleImageInsert} disabled={!imageUrl.trim()}>
                  Insert Image
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      
      <div
        ref={editorRef}
        contentEditable
        className="min-h-[400px] p-4 focus:outline-none prose prose-sm max-w-none text-black [&_ul]:list-disc [&_ul]:ml-6 [&_ol]:list-decimal [&_ol]:ml-6 [&_li]:text-black [&_li]:mb-1"
        style={{
          lineHeight: '1.7',
          fontFamily: 'var(--font-body)',
        }}
        onInput={handleInput}
        onMouseUp={updateActiveFormats}
        onKeyUp={updateActiveFormats}
        suppressContentEditableWarning={true}
        data-placeholder={placeholder}
      />
    </div>
  );
};